import { Autocomplete, Box, Button, Grid, TextField } from "@mui/material";
import React, { useEffect } from "react";
import { useRecoilValue } from "recoil";
import { ImportBusService } from "../../../Component/Api/Import/ImportBusService";
import { ImportService } from "../../../Component/Api/Import/ImportService";
import { FormField } from "../../../Component/Common/Components/FormField";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { UploadButton } from "../../../Component/Common/Components/UploadButton/UploadButton";
import { ExportKeyValue, FileUploadRequestParameters, UploadRequestParameters } from "../../../Component/Model/Dto/Upload";
import { DataImportType, UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";
import { dataImportTypeLookupVal, getUploadFileName, ImportCommonFunctions, IOptions } from "../../../Helpers/ImportHelper";
import { FileUploadClient } from "../../../Services/SignalR/FileUploadClient";
import { UploadFileProgressClient } from "../../../Services/SignalR/UploadFileProgressHub";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { UserAtom } from "../../../State/user.Atom";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { MDAlert } from "../../../Component/Common/Components/MDAlert";
import { importAllowedFileTypes, importBusTypes } from "./ImportExport";
import { CoreImportService } from "../../../Component/Api/Import/CoreImportService";
import { emptyGuid } from "../../../Helpers/typescriptHelper";
import { LinearProgressWithLabel } from "../../EmploymentEquity/GeneralInformationPage/GeneralInformation";
import { ProgressBar } from "react-bootstrap";
import { EEScorecardAtom } from "../../../State/EEScorecard.Atom";
import { DynamicCsvRequest } from "../../../Component/Model/Dto/DynamicCsv";
import { DynamicCsvService } from "../../../Component/Api/DynamicCsv/DynamicCsvService";
import { DateFormatter } from "../../../Component/Model/helpers";
import { VerificationWorkflowService } from "../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import { toast } from "react-toastify";
import {ImportSource} from "../../../Component/Model/Enum/ImportEnum";
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { EmployeeStructureSection } from "../../../Component/Common/Constants/UserActivityTrackingSections";


export enum DataImportTypesCode {
  name,
  id
}

interface IProps {
  showExportButton: boolean;
  scorecardId?: string;
  uploadType: string;
  controlsEnabled: boolean;
  showHelpfileDownload?: boolean;
  importBus: boolean;
  showYearToAdd?: boolean;
  coreImportEnabled: boolean;
  typeId: DataImportType;
  section?: string;
}


export const ImportPopup = (props: IProps) => {
  const user = useRecoilValue(UserAtom);
  const scorecardDto = useRecoilValue(EEScorecardAtom);
  const yearBoxLabel = props.uploadType === "EESalary" ? "Reporting Range" : "Year";

  const [numberofRowsCorrect, setNumberofRowsCorrect] = React.useState<number>(0);
  const [numberofRowsFailed, setNumberofRowsFailed] = React.useState<number>(0);
  const [showExportButton, setShowExportButton] = React.useState<boolean>(props.showExportButton);
  const [showHelpfileDownload] = React.useState<boolean>(props.showHelpfileDownload ?? true);
  const [importOverwrite, setImportOverwrite] = React.useState<IOptions>({ Id: 0, Name: "Select" });
  const [errorLogs, setErrorLogs] = React.useState<string[]>([]);
  const [uploadFileProgress] = React.useState<UploadFileProgressClient>(new UploadFileProgressClient());
  const [percentageUpload, setPercentageUpload] = React.useState<number>(0);
  const [isProcessingFile, setIsProcessingFile] = React.useState<boolean>(false);
  const [importSuccessful, setImportSuccessful] = React.useState<boolean>(true);
  const [username] = React.useState<string>(user.username || "");
  const [fileFormatNotAllowed, setFileFormatNotAllowed] = React.useState<boolean>(false);
  const [showYearToAdd] = React.useState<boolean | undefined>(props.showYearToAdd);
  const [yearToAdd, setYearToAdd] = React.useState<number>(2022);
  const [reportRange, setReportRange] = React.useState<string>();
  const [scorecardReportPeriodDateFrom] = React.useState<Date>(scorecardDto.ReportPeriodDateFrom);
  const [scorecardReportPeriodDateTo] = React.useState<Date>(scorecardDto.ReportPeriodDateTo);
  const [showErrorLog, setShowErrorLog] = React.useState({
    showErrorLog: false,
    errorLogUrl: null
  })
  const fileUploadClient = new FileUploadClient();
  let intervalID: any;
  const hub = useSignalRConnection("ImportHub");
  const importIdentifier = `${username}-${props.scorecardId ?? emptyGuid}-${DataImportType[props.typeId]}`;

  const signalR = async () => {
      try {
          if(!hub.isConnected)
          {
              await hub.start();
          }

          hub.on(`importProgress-${importIdentifier}`, (data: any) => {
              setPercentageUpload(data.progress)
          })

          hub.on(`importProgressFailed-${importIdentifier}`, (data: any) => {
              setNumberofRowsCorrect(data.NumberOfRowsCorrect);
              setNumberofRowsFailed(data.NumberOfRowsFailed);
              setPercentageUpload(data.progress)
              setImportSuccessful(false);
              setErrorLogs(data.Log ?? []);
              setShowExportButton(true);
              setShowErrorLog({
                  showErrorLog: true,
                  errorLogUrl: data.ErrorBlobUrl
              })
          })

          hub.on(`importProgressSuccess-${importIdentifier}`, (data: any) => {
              setNumberofRowsCorrect(data.NumberOfRowsCorrect);
              setNumberofRowsFailed(data.NumberOfRowsFailed);
              setPercentageUpload(data.progress);
              setIsProcessingFile(false);
              setImportSuccessful(true);
              setErrorLogs(data.Log ?? []);
              setShowExportButton(true);
          })
      } catch (error) {
          console.error("SignalR connection failed:", error);
      }
  }


  const setReportFormattedRange = () => {
    const formattedStartRange = DateFormatter(scorecardReportPeriodDateFrom.toString());
    const formattedEndRange = DateFormatter(scorecardReportPeriodDateTo.toString());

    const formattedDateRange = `${formattedStartRange} - ${formattedEndRange}`;

    setReportRange(formattedDateRange);
  }

  useEffect(() => {
    setReportFormattedRange();
    if (props.coreImportEnabled) {
      signalR();
    } else {
      fileUploadClient.joinGroup(
        username,
        UpdateUploadStatus
      );
      uploadFileProgress.joinGroup(
        username,
        UpdateUploadStatus
      );
      removeKey();
    }

    return () => {
      if (!props.coreImportEnabled) {
        uploadFileProgress.removeListeners(UpdateUploadStatus);
        fileUploadClient.removeListeners(UpdateUploadStatus);
      }
    };

  }, []);


  const uploadStuckCheck = () => {

    if (props.scorecardId !== undefined) {

      const parameters: UploadRequestParameters = {
        ScorecardId: props.scorecardId,
        type: props.uploadType
      }
      ImportService.UploadFileStuckCheck(parameters).then((res: any) => {
        if (res.data.Status.includes('Completed')) {
          clearInterval(intervalID);
          setNumberofRowsCorrect(res.data.NumberOfRowsCorrect);
          setNumberofRowsFailed(res.data.NumberOfRowsFailed);
          setIsProcessingFile(false);
          setImportSuccessful(res.data.WasSuccessful);
          setErrorLogs(res.data.Log ?? []);
          setShowExportButton(!res.data.WasSuccessful);
        }
        else if (res.data.Status.includes('Busy')) {
          setIsProcessingFile(true);
        }
        else if (res.data.Status.includes('Processing')) {
          setIsProcessingFile(true);
        }
        else if (res.data.Status.includes('Started')) {
          setIsProcessingFile(true);
        }
        else if (res.data.Status.includes('Failed')) {
          clearInterval(intervalID);
          removeKey();
          setNumberofRowsCorrect(res.data.NumberOfRowsCorrect);
          setNumberofRowsFailed(res.data.NumberOfRowsFailed);
          setIsProcessingFile(false);
          setImportSuccessful(res.data.WasSuccessful);
          setErrorLogs(res.data.Log ?? []);
          setShowExportButton(!res.data.WasSuccessful);
        }
      });
    }

  }

  const signalRcheck = () => {
    var newIntervalId = setInterval(uploadStuckCheck, 5000);
    intervalID = newIntervalId;
  }

  const UpdateUploadStatus = (message: any): void => {
    if (props.importBus && importBusTypes.some(item => item.toLowerCase() === props.uploadType.toLowerCase())) {
      setNumberofRowsCorrect(message.NumberOfRowsCorrect);
      setNumberofRowsFailed(message.NumberOfRowsFailed);
      setIsProcessingFile(message.Status === 0);
      setImportSuccessful(message.Status === 1);
      setShowExportButton(message.Status === 2);
      setPercentageUpload(message.ProgressPercentage);
    }
    else {
      if (message === "Upload processing... ") {
      } else {
        clearInterval(intervalID);
        setNumberofRowsCorrect(message.ImportResult.NumberOfRowsCorrect);
        setNumberofRowsFailed(message.ImportResult.NumberOfRowsFailed);
        setIsProcessingFile(false);
        setImportSuccessful(message.ImportResult.WasSuccessful);
        setErrorLogs(message.ImportResult.Log)
        setShowExportButton(!message.ImportResult.WasSuccessful);
      }
    }
  };

  const onImportClick = () => {
    setErrorLogs([]);
    setNumberofRowsFailed(0);
    setShowExportButton(false);
  };

  const importLog = (actionType: number, actionDescription: string) => {
    UserActivityService.createActivityLog({
        section: EmployeeStructureSection + props.section,
        actionType: actionType,
        actionDescription: actionDescription,
        scorecardId: props.scorecardId,
        requestUrl: window.location.href
    });
  }

  const getImportTemplate = (e: any) => {
    if (props.uploadType === "EESalary") {
      handleDownload();
      importLog(UserActivityActionType.Download, "Download salary template file")
    }
    else {
      ImportService.DownloadImportTemplate(props.scorecardId ?? emptyGuid, getUploadFileName(props.uploadType)); 
      props.section === "Employees" && importLog(UserActivityActionType.Download, "Download employee structure template file") 
    }
  };

  const handleDownload = async () => {
    const request: DynamicCsvRequest = {
      salaryStart: scorecardReportPeriodDateFrom,
      salaryEnd: scorecardReportPeriodDateTo,
      onlyShowCTC: false
    }

    const result = await DynamicCsvService.GenerateCsv(request).catch((err) => { console.log(err) });
    if (result) {
      const csvData = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(csvData);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'EESalary.csv';
      link.click();
    }
  }

  const getImportHelpFile = (e: any) => {
    const sectionId = dataImportTypeLookupVal(props.uploadType, DataImportTypesCode.name, DataImportTypesCode.id);
    if (sectionId?.toString() !== undefined) {
      ImportService.DownloadImportHelpfile(props.scorecardId ?? emptyGuid, sectionId.toString());
    }
  };

  const removeKey = () => {
    if (props.scorecardId !== undefined) {
      const parameters: UploadRequestParameters = {
        ScorecardId: props.scorecardId,
        type: props.uploadType
      }
      ImportService.RemoveRedisKeyCheck(parameters);
    }

  }
  const changeOverwrite = (newValue: IOptions) => {
    setImportOverwrite({ Id: newValue.Id, Name: newValue.Name })
  };

  const percentageUploadChange = (progress: number) => {
    setPercentageUpload(progress);
  };


  const ExportErrorLogs = () => {
    var convertedArray = ["Error Log\r\n"];

    for (var i = 0; i < errorLogs.length; i++) {
      convertedArray.push(errorLogs[i] + "\r\n");
    }

    var csvContent = "data:text/csv;charset=utf-8," + convertedArray;

    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      props.uploadType + "_ImportErrors.csv"
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const checkForInProgressImportForScorecard = async (scorecardId: string): Promise<boolean> => {
    try {
        const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecardId);
        return !!result.data;
    } catch (error) {
        console.log(error);
        toast.error("An error has occurred");
        return false;
    }
};

  const checkForInProgressImportForUser = async (importType: number): Promise<boolean> => {
    try {
      const result = await VerificationWorkflowService.IsImportInProgressForUser(importType);
      return result.data;
    } catch (error) {
      console.log(error);
      toast.error("An error has occurred");
      return false;
    }
  };

  const onChange = async (e: any) => {
    onImportClick();
    let file = e.target.files[0];
    setIsProcessingFile(true);
    setFileFormatNotAllowed(false);

    if (props.coreImportEnabled) {
      try {
        const parameters: ExportKeyValue[] = [];
        if (showYearToAdd) {
          parameters.push({ key: "SalaryYear", value: yearToAdd.toString() });
        }
        let isInProgress = false;
        if(props.scorecardId)
        {
          isInProgress = await checkForInProgressImportForScorecard(props.scorecardId!);
        }
        else{
          isInProgress = await checkForInProgressImportForUser(props.typeId);
        }


        if (isInProgress){
            toast.error(`An import is already in progress. Please try again later`)
            setIsProcessingFile(false); 
            return; 
        }
        const request = {
          importType: props.typeId,
          scorecardId: props.scorecardId,
          importSource: ImportSource.Unspecified,
          importAction: importOverwrite.Id,
          file: file,
          parameters: parameters
        };
        await CoreImportService.ImportFile(request);
        const description = props.section === "Employees" ? "Import employee structure file" : "Import salary file";
        importLog(UserActivityActionType.Import, description);
      } catch (error) {
        console.log(error);
      }
    } else {
      if (importAllowedFileTypes.some(item => item.toLocaleLowerCase() === file.name.split('.').pop().toLowerCase())) {
        if (props.importBus && importBusTypes.some(item => item.toLowerCase() === props.uploadType.toLowerCase())) {
          //Add import list
          try {
            await ImportBusService.UploadFile({
              ImportType: props.uploadType,
              ScorecardId: props.scorecardId ?? emptyGuid,
              ImportAction: importOverwrite.Id,
              file: file,
              setPercentageUpload: (progress: number) => percentageUploadChange(progress),
            });
          } catch (error) {
            console.log(error);
          }
        }
        else {
          const parameters: FileUploadRequestParameters = {
            ScorecardId: props.scorecardId ?? emptyGuid,
            ImportType: props.uploadType,
            ImportAction: importOverwrite.Id,
            type: props.uploadType
          }
          try {
            await ImportService.UploadFile(
              parameters,
              file,
              (progress: number) => percentageUploadChange(progress),
            );
            signalRcheck();
          } catch (error) {
            console.log(error);
          }
        }
      }
      else {
        setFileFormatNotAllowed(true);
        setIsProcessingFile(false);
      }
    }
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYearToAdd(+event.target.value);
  };

  const onDownload = () => {
    const link = document.createElement("a");
    link.download = `errorLog.csv`;
    link.href = showErrorLog.errorLogUrl!;
    link.click();
  };
  const yearBoxValue = props.uploadType === "EESalary" ? reportRange : yearToAdd;

  return (
    <>
      <Grid container >
        <Grid item xs={12} lg={12}>
          {fileFormatNotAllowed && <MDAlert color="error" dismissible>
            <MDTypography component='span' variant='body' fontWeight='regular' color="white">
              The file is not in a valid file format for upload. Please use '.csv' files only.
            </MDTypography>
          </MDAlert>}
        </Grid>
        <Grid item xs={7} lg={7}>
          <MDBox >
            <MDBox>
              <Autocomplete
                style={{ paddingTop: "5px" }}
                options={ImportCommonFunctions(props.uploadType)}
                isOptionEqualToValue={(option, value) => value.Id === option.Id}
                getOptionLabel={option => option.Name}
                defaultValue={importOverwrite}
                onChange={(e: any, newValue: any) => {
                  if (newValue) {
                    changeOverwrite(newValue)
                  }
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.Id} value={option.Id}>
                      {option.Name}
                    </li>
                  );
                }}
                renderInput={(params) => <FormField variant="outlined" label='Import task' {...params} type='text' required />}
              />
              {showYearToAdd === true && (
                <Box mt={3}>
                  <TextField
                    id="txt-yearToAdd-withValue"
                    label={yearBoxLabel}
                    sx={{ width: '300px' }}
                    onChange={handleYearChange}
                    value={yearBoxValue}
                  />
                </Box>
              )}
            </MDBox>
            {(importOverwrite.Id > 0 ||
              props.uploadType ===
              DataImportType[DataImportType.IndividualSalary]) && <MDBox mt={3.5}>
                <UploadButton
                  id='upload-file' onChange={onChange} type='file'
                  accept=".csv"
                  IconView={FileUploadIcon} text='Select file'
                  color="info"
                  disabled={false}>
                </UploadButton>
              </MDBox>}
          </MDBox>
        </Grid>
      </Grid>
      <Grid container >
        <Grid item xs={12} lg={12}>
          {props.coreImportEnabled && (
            <MDBox mt={3} >
              <Grid container >
                <Grid item xs={12} lg={12}>
                  <LinearProgressWithLabel value={percentageUpload} />
                </Grid>
              </Grid>
            </MDBox>
          )}
          {(isProcessingFile && !props.coreImportEnabled) && (
            <MDBox mt={3} >
              <Grid container >
                <Grid item xs={12} lg={12}>
                  <ProgressBar animated now={100} variant="success" />
                </Grid>
              </Grid>
            </MDBox>
          )}
          <MDBox>
            {(!importSuccessful && errorLogs.length > 0) && (
              <MDAlert color="error" mt={1}>
                <Grid container >
                  <Grid item xs={12} lg={12}>
                    <MDTypography component='span' fontWeight='bold' fontSize="1.35rem" color="white">
                      Import Failed
                    </MDTypography>
                    {numberofRowsFailed > 0 && (
                      <MDBox mb={2}>
                        <MDTypography component='span' fontWeight='regular' color="white">
                          Number Of Rows Failed: {numberofRowsFailed}
                        </MDTypography>
                      </MDBox>
                    )}
                    {(showErrorLog.errorLogUrl !== null && showErrorLog.showErrorLog) && (
                      <MDBox mb={2}>
                      <Button
                        variant='contained'
                        onClick={() => {
                          onDownload()
                        }}
                      >
                        Download Error Log
                      </Button>
                      </MDBox>
                    )}
                    {errorLogs.map((error) => (
                      <MDTypography component='p' variant='body' fontWeight='regular' color="white" mb={1}>
                        {error}
                      </MDTypography>
                    ))}
                  </Grid>
                </Grid>
              </MDAlert>
            )}
            {numberofRowsCorrect > 0 && (
              <MDAlert color="success" mt={1}>
                <Grid container >
                  <Grid item xs={12} lg={12}>
                    <MDBox>
                      <MDTypography component='span' variant='body' fontWeight='regular' color="white">
                        Number Of Rows Correct: {numberofRowsCorrect}
                      </MDTypography>
                    </MDBox>
                    {errorLogs.length > 0 && errorLogs.map((message) => (
                      <MDTypography component='p' variant='body' fontWeight='regular' color="white" mb={1}>
                        {message}
                      </MDTypography>
                    ))}
                  </Grid>
                </Grid>
              </MDAlert>
            )}
          </MDBox>

          {showExportButton &&
            (!importSuccessful && showErrorLog.errorLogUrl === null) && (
              <MDBox mt={1}>
                <Button
                  onClick={ExportErrorLogs}
                  color="primary"
                  variant="contained"
                >
                  Download Error Log
                </Button>
              </MDBox>
            )}
        </Grid>
        <Grid item xs={12} lg={12}>
          {showHelpfileDownload === true && (
            <MDBox mt={4} color="white">
              <MDAlert color='light'>
                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>

                  <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={getImportHelpFile} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                    for import instructions</p>

                  <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={getImportTemplate} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                    to download the template file</p>

                </MDTypography>
              </MDAlert>
            </MDBox>
          )}
        </Grid>
      </Grid>
    </>
  );
}
